import { useCallback } from 'react';
import { useSectionType } from '../../sections/SectionTypeContext.tsx';
import { type Widget, type WidgetType } from '../Widget.ts';
import { WidgetBlockLabelsLogic } from './WidgetBlockLabelsLogic.ts';
import { useWidgetCreationDefaults } from './WidgetCreationDefaultsContext.tsx';
import { WidgetDefaultRequiredLogic } from './WidgetDefaultRequiredLogic.ts';
import { WidgetRepository } from './WidgetRepository.ts';

/**
 * Factory per la creazione di nuovi widget nel builder: incapsula
 * `WidgetRepository.getInitial` applicando i default che dipendono dal tipo
 * di sezione corrente e dal Popover "Creazione nuovi Elementi"
 * (obbligatorietà ed etichetta a blocco, vedi `WidgetDefaultRequiredLogic` e
 * `WidgetBlockLabelsLogic`).
 *
 * Da preferire alla chiamata diretta di `getInitial` nei punti di creazione
 * della UI, così i default di sezione non vengono dimenticati.
 */
export interface ICreateInitialWidgetOptions {
  /**
   * Widget creato come cella personalizzata di una Tabella di Input:
   * l'etichetta in cella è nascosta, quindi il default "etichetta a blocco"
   * non viene applicato (l'obbligatorietà di default resta, come per le
   * colonne).
   */
  isTableCell?: boolean;
}

export function useCreateInitialWidget() {
  const { sectionType } = useSectionType();
  const { defaultRequired, blockLabels } = useWidgetCreationDefaults();

  return useCallback(
    (
      widgetType: WidgetType,
      partialWidget?: Partial<Widget>,
      options?: ICreateInitialWidgetOptions,
    ) => {
      const requiredDefaults =
        WidgetDefaultRequiredLogic.getInitialWidgetPartial(
          widgetType,
          sectionType,
          defaultRequired,
        );
      const blockLabelDefaults = WidgetBlockLabelsLogic.getInitialWidgetPartial(
        widgetType,
        sectionType,
        blockLabels && !options?.isTableCell,
      );

      // Il partial del chiamante ha precedenza sui default di sezione;
      // `rules` e `layout` (le uniche chiavi dei default) vanno però uniti
      // campo per campo, altrimenti un chiamante che passa altre regole o
      // altre opzioni di layout cancellerebbe i default.
      const rules = {
        ...requiredDefaults?.rules,
        ...(partialWidget && 'rules' in partialWidget
          ? partialWidget.rules
          : undefined),
      };
      const layout = {
        ...blockLabelDefaults?.layout,
        ...(partialWidget && 'layout' in partialWidget
          ? partialWidget.layout
          : undefined),
      };

      return WidgetRepository.getInitial(widgetType, {
        ...partialWidget,
        ...(Object.keys(rules).length > 0 ? { rules } : undefined),
        ...(Object.keys(layout).length > 0 ? { layout } : undefined),
      });
    },
    [sectionType, defaultRequired, blockLabels],
  );
}
